/**
 * Bases de mapa conmutables y la capa de etiquetas de calles.
 *
 * Sobre Stadia: el proveedor autoriza por dominio y rechaza los no registrados
 * con `401` cuyo cuerpo es un PNG válido de 512x512 que dice "401 Error". Un
 * `<img>` con ese cuerpo dispara `load`, no `error`, así que cualquier cadena de
 * respaldo colgada de `tileerror` nunca avanza: el mapa se llena de teselas de
 * error mientras la interfaz reporta éxito. La única detección que funciona es
 * pedir una tesela con `fetch` y leer `response.ok` ANTES de instalar la capa.
 */

import L from 'leaflet';
import { STADIA_API_KEY } from '../config.js';

const ESRI_IMAGERY =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const ESRI_LABELS =
  'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';
const OSM_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const STADIA_SATELLITE = 'https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.jpg';

export function createEsriLayer() {
  return L.tileLayer(ESRI_IMAGERY, {
    maxZoom: 21,
    maxNativeZoom: 19,
    crossOrigin: true,
    attribution: 'Esri World Imagery',
  });
}

export function createLabelsLayer() {
  return L.tileLayer(ESRI_LABELS, {
    maxZoom: 21,
    maxNativeZoom: 19,
    crossOrigin: true,
  });
}

export function createOsmLayer() {
  return L.tileLayer(OSM_TILES, {
    maxZoom: 19,
    maxNativeZoom: 19,
    crossOrigin: true,
    attribution: 'OpenStreetMap',
  });
}

export function createStadiaLayer(apiKey = STADIA_API_KEY) {
  const suffix = apiKey ? `?api_key=${apiKey}` : '';
  return L.tileLayer(STADIA_SATELLITE + suffix, {
    maxZoom: 21,
    maxNativeZoom: 20,
    crossOrigin: true,
    attribution:
      '© CNES/Airbus DS/PlanetObserver · Stadia Maps · OpenMapTiles · OpenStreetMap',
  });
}

/**
 * Comprueba que el proveedor realmente sirve teselas a este dominio.
 *
 * @returns {Promise<boolean>} `true` si sirve, `false` si el proveedor rechaza.
 *   Si el `fetch` LANZA (offline, bloqueado por una extensión, CORS), devuelve
 *   `true`: una sonda bloqueada no dice nada sobre el proveedor, y negar la capa
 *   por eso sería peor que instalarla.
 */
export async function probeTileProvider(sampleTileUrl) {
  try {
    const response = await fetch(sampleTileUrl, { method: 'GET', cache: 'no-store' });
    return response.ok;
  } catch {
    return true;
  }
}

/** URL de una tesela concreta de Stadia, para usar como sonda. */
export function stadiaProbeUrl(apiKey = STADIA_API_KEY) {
  const suffix = apiKey ? `?api_key=${apiKey}` : '';
  return `https://tiles.stadiamaps.com/tiles/alidade_satellite/13/2620/4640.jpg${suffix}`;
}

/**
 * Gestiona qué base está activa y mantiene coherente la capa de etiquetas
 * (que sólo tiene sentido sobre satélite, no sobre el mapa OSM de calles).
 */
export class BasemapController {
  constructor(map) {
    this.map = map;
    this.labels = createLabelsLayer();
    this.layers = {
      esri: createEsriLayer(),
      osm: createOsmLayer(),
      stadia: null,
    };
    this.active = null;
    this.activeId = null;
    this.labelsEnabled = true;
    this.stadiaUsable = null;
  }

  /** Instala la base pedida. Devuelve el id realmente aplicado, que puede diferir. */
  async select(id) {
    let target = id;

    if (target === 'stadia') {
      if (this.stadiaUsable === null) {
        this.stadiaUsable = await probeTileProvider(stadiaProbeUrl());
      }
      if (!this.stadiaUsable) {
        // El dominio no está autorizado. Sin esta comprobación el mapa mostraría
        // un mosaico de teselas "401 Error" sin ningún síntoma de fallo.
        target = 'esri';
      } else if (!this.layers.stadia) {
        this.layers.stadia = createStadiaLayer();
      }
    }

    const layer = this.layers[target] || this.layers.esri;
    if (this.active) this.map.removeLayer(this.active);
    layer.addTo(this.map);
    if (layer.bringToBack) layer.bringToBack();
    this.active = layer;
    this.activeId = target;
    this.syncLabels();
    return target;
  }

  /** Las etiquetas van sobre satélite; sobre OSM sobran porque ya trae las suyas. */
  syncLabels() {
    const isSatellite = this.activeId !== 'osm';
    if (this.labelsEnabled && isSatellite) this.labels.addTo(this.map);
    else this.map.removeLayer(this.labels);
  }

  toggleLabels() {
    this.labelsEnabled = !this.labelsEnabled;
    this.syncLabels();
    return this.labelsEnabled;
  }
}
